import React, { useState } from 'react';
import { Plus, Minus, ExternalLink, Sparkles } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations, TranslationKey } from '@/translations/translations';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PromotionItem } from '@/utils/messageParser/types';
import { openURL } from '@/utils/navigationUtils';

interface PromotionsListMessageProps {
  promotions: PromotionItem[];
  showTitle?: boolean; // Optional prop to show the title
}

const PromotionsListMessage: React.FC<PromotionsListMessageProps> = ({ promotions, showTitle = false }) => {
  const { language } = useLanguage();
  // Auto-expand cards that have details
  const [expandedCards, setExpandedCards] = useState<Set<number>>(() => {
    const initialExpanded = new Set<number>();
    promotions.forEach((promotion, index) => {
      if (promotion.details && promotion.details.trim() !== '') {
        initialExpanded.add(index);
      }
    });
    return initialExpanded;
  });

  const t = (key: TranslationKey): string => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const getTypeLabel = (type: string | undefined) => {
    if (!type) return t('promotions.type.other');
    switch (type.toLowerCase()) {
      case 'aftersale':
        return t('promotions.type.aftersale');
      case 'financing':
      case 'financement':
        return t('promotions.type.financing');
      case 'leasing':
      case 'location':
        return t('promotions.type.leasing');
      case 'oemoffer':
      case 'manufacturer':
      case 'manufacturier':
        return t('promotions.type.oemOffer');
      case 'oemrebates':
        return t('promotions.type.oemRebates');
      case 'dealer':
      case 'concessionnaire':
        return t('promotions.type.dealer');
      case '0financing':
        return t('promotions.type.zeroFinancing');
      case 'other':
      case 'autre':
      default:
        return t('promotions.type.other');
    }
  };

  const getTypeColor = (type: string | undefined) => {
    if (!type) return 'bg-gray-100 text-gray-800';
    switch (type.toLowerCase()) {
      case 'aftersale':
        return 'bg-blue-100 text-blue-800';
      case 'financing':
      case 'financement':
        return 'bg-green-100 text-green-800';
      case 'leasing':
      case 'location':
        return 'bg-purple-100 text-purple-800';
      case 'oemoffer':
      case 'manufacturer':
      case 'manufacturier':
        return 'bg-orange-100 text-orange-800';
      case 'oemrebates':
        return 'bg-amber-100 text-amber-800';
      case 'dealer':
      case 'concessionnaire':
        return 'bg-indigo-100 text-indigo-800';
      case '0financing':
        return 'bg-emerald-100 text-emerald-800';
      case 'other':
      case 'autre':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handlePromotionClick = async (promotion: PromotionItem) => {
    if (promotion.link) {
      await openURL(promotion.link);
    }
  };

  const toggleCardExpansion = (index: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCards(newExpanded);
  };

  return (
    <TooltipProvider>
      <div className="space-y-3 mb-6">
        {/* Optional title for aftersale promotions */}
        {showTitle && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              {t('promotions.aftersale.title')}
            </h3>
          </div>
        )}
        {promotions.map((promotion, index) => {
          const isExpanded = expandedCards.has(index);
          const hasDetails = promotion.details && promotion.details.trim() !== '';

          return (
            <div key={index} className="bg-white rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
              {/* Main promotion info row */}
              <div className="flex items-start gap-4">
                {/* Column 1: Square Promotion Image with Tooltip */}
                <div className="flex-shrink-0">
                  {promotion.image ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <img 
                          src={promotion.image.startsWith('http') ? promotion.image : `https://${promotion.image}`}
                          alt={promotion.title}
                          className={`object-cover rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 ${promotion.link ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                          style={{ height: '80px', width: '80px' }}
                          onClick={() => handlePromotionClick(promotion)}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const placeholder = target.nextElementSibling as HTMLElement;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="p-0 d2cbot-bot_border-0 bg-transparent shadow-lg">
                        <img 
                          src={promotion.image.startsWith('http') ? promotion.image : `https://${promotion.image}`}
                          alt={promotion.title}
                          className="rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 shadow-lg"
                          style={{ height: '125px', width: 'auto' }}
                        />
                      </TooltipContent>
                    </Tooltip>
                  ) : !promotion.link ? (
                    // Animated red promotional banner when no image and no link
                    <div 
                      className="rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-red-500 to-red-600"
                      style={{ height: '80px', width: '80px' }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_ease-in-out_infinite]" />
                      <Sparkles className="w-8 h-8 text-white animate-pulse relative z-10" />
                    </div>
                  ) : (
                    <div 
                      className={`bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 flex items-center justify-center text-gray-400 cursor-pointer hover:opacity-80 transition-opacity`}
                      style={{ height: '80px', width: '80px' }}
                      onClick={() => handlePromotionClick(promotion)}
                    >
                      <Sparkles className="w-6 h-6" />
                    </div>
                  )}
                </div>

                {/* Column 2: Title and Type */}
                <div className="flex-grow flex flex-col">
                  <div className="mb-2">
                    <Badge className={`text-xs px-2 py-1 w-fit ${getTypeColor(promotion.type || promotion.Type)}`}>
                      {getTypeLabel(promotion.type || promotion.Type)}
                    </Badge>
                  </div>
                  <div className="text-sm font-bold text-gray-900 leading-tight mb-3">
                    {promotion.title}
                  </div>
                </div>
              </div>

              {/* Bottom section with More/Less button on left and Action button on right */}
              <div className="flex items-center justify-between mt-3">
                {/* More/Less toggle button on left side */}
                {hasDetails ? (
                  <button
                    onClick={() => toggleCardExpansion(index)}
                    className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    {isExpanded ? (
                      <>
                        <Minus className="w-3 h-3" />
                        {t('promotions.less')}
                      </>
                    ) : (
                      <>
                        <Plus className="w-3 h-3" />
                        {t('promotions.more')}
                      </>
                    )}
                  </button>
                ) : (
                  <div></div> // Empty div to maintain flex layout
                )}

                {/* Action Button on the right */}
                <Button
                  onClick={() => handlePromotionClick(promotion)}
                  size="sm"
                  className="!bg-red-600 hover:!bg-red-700 text-white hover:!text-white px-3 py-1 text-xs rounded-md transition-colors duration-200 h-7 flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  {t('promotions.view')}
                </Button>
              </div>

              {/* Expanded content */}
              {hasDetails && isExpanded && (
                <div className="mt-4 pt-3 d2cbot-bot_border-t d2cbot-bot_border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    {t('promotions.description')}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg">
                    {promotion.details}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

export default PromotionsListMessage;